import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { UKOnly } from '@iwIntlProvider/CountryWrappers';
import { Text } from '@iwoca/orion-ui';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import messages from './RepaymentCard.messages';
import styles from './RepaymentCard.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import cardStyles from '../../../styles/variables/cards.module.css';
import { LogoSVG } from '../../../svgComponents/logoSVG';

const RepaymentCard = () => {
    const stateKey = useStateKey();
    const { data: repaymentDetails } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: ({ data }) => {
            const accounts = data!.accounts;
            if (!accounts.length) return null;

            const accountsWithBankTransferDetails = accounts.filter(
                (account) => account.repayment_details!.bank_transfer_details
            );
            const latestAccount =
                accountsWithBankTransferDetails[
                    accountsWithBankTransferDetails.length - 1
                ];
            if (!latestAccount.repayment_details) return null;

            return latestAccount.repayment_details!.bank_transfer_details!;
        }
    });

    if (!repaymentDetails) return null;

    const {
        owner_name: referenceName,
        // @ts-expect-error This does exist in the response, the schema is wrong.
        iban,
        reference: referenceID,
        account_number: accountNumber,
        sort_code: sortCode
    } = repaymentDetails;

    const repaymentCardStyles = classNames(
        styles.RepaymentCard,
        cardStyles.card
    );

    return (
        <div
            className={repaymentCardStyles}
            data-testid="repayment-details-information"
        >
            <div className={styles.content}>
                <div className={styles.cardTitle}>
                    <LogoSVG className={styles.logo} />
                    <div className={styles.repaymentDetails}>
                        <FormattedMessage {...messages.repaymentDetails} />
                    </div>
                </div>
                <div className={styles.line}>
                    <FormattedMessage {...messages.payee} />
                    &#58;{' '}
                    <span className={classNames(styles.value, 'fs-exclude')}>
                        {referenceName}
                    </span>
                </div>
                {iban ? (
                    <>
                        <div className={styles.line}>
                            <FormattedMessage {...messages.iban} />
                            &#58;{' '}
                            <span
                                className={classNames(
                                    styles.value,
                                    'fs-exclude'
                                )}
                            >
                                {iban}
                            </span>
                        </div>
                        <div className={styles.line}>
                            <FormattedMessage {...messages.reference} />
                            &#58;{' '}
                            <span
                                className={classNames(
                                    styles.value,
                                    'fs-exclude'
                                )}
                            >
                                {referenceID}
                            </span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.line}>
                            <FormattedMessage {...messages.accountNumber} />
                            &#58;{' '}
                            <span
                                className={classNames(
                                    styles.value,
                                    'fs-exclude'
                                )}
                            >
                                {accountNumber}
                            </span>
                        </div>
                        <div className={styles.line}>
                            <FormattedMessage {...messages.sortCode} />
                            &#58;{' '}
                            <span
                                className={classNames(
                                    styles.value,
                                    'fs-exclude'
                                )}
                            >
                                {sortCode}
                            </span>
                            <UKOnly>
                                <Text className="mt-s text-s font-bold">
                                    <FormattedMessage
                                        {...messages.chapsRepayments}
                                    />
                                </Text>
                            </UKOnly>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default RepaymentCard;
